import React, { useState, useEffect, useCallback } from 'react';
import './ErrorLog.css';

interface ErrorLogEntry {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  type: string;
  message: string;
  details?: {
    endpoint?: string;
    method?: string;
    requestBody?: any;
    queryParams?: any;
    flightNumber?: string;
    airlineCode?: string;
    route?: {
      origin?: string;
      destination?: string;
      departureDate?: string;
    };
    basePrice?: number;
    errorCode?: string;
    stack?: string;
    [key: string]: any;
  };
  userAgent?: string;
  ip?: string;
}

interface ErrorLogStats {
  total: number;
  byLevel: {
    error: number;
    warning: number;
    info: number;
  };
  byType: Record<string, number>;
  recentErrors: number;
}

const ErrorLog: React.FC = () => {
  const [logs, setLogs] = useState<ErrorLogEntry[]>([]);
  const [stats, setStats] = useState<ErrorLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{
    level?: 'error' | 'warning' | 'info';
    type?: string;
    limit: number;
  }>({ limit: 100 });
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const params = new URLSearchParams();
      
      if (filter.level) params.append('level', filter.level);
      if (filter.type) params.append('type', filter.type);
      params.append('limit', filter.limit.toString());

      const response = await fetch(`${apiUrl}/api/errors?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch error logs: ${response.statusText}`);
      }
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/errors/stats`);
      if (!response.ok) {
        throw new Error(`Failed to fetch stats: ${response.statusText}`);
      }
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchLogs();
        fetchStats();
      }, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchLogs, fetchStats]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getLevelBadgeClass = (level: string) => {
    switch (level) {
      case 'error':
        return 'level-badge error';
      case 'warning':
        return 'level-badge warning';
      case 'info':
        return 'level-badge info';
      default:
        return 'level-badge';
    }
  };

  return (
    <div className="error-log-container">
      <header className="error-log-header">
        <h1>Error Log</h1>
        <p>Real-time error tracking and troubleshooting</p>
      </header>

      {stats && (
        <div className="error-log-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Logs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value error">{stats.byLevel.error}</div>
            <div className="stat-label">Errors</div>
          </div>
          <div className="stat-card">
            <div className="stat-value warning">{stats.byLevel.warning}</div>
            <div className="stat-label">Warnings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value info">{stats.byLevel.info}</div>
            <div className="stat-label">Info</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.recentErrors}</div>
            <div className="stat-label">Errors (24h)</div>
          </div>
        </div>
      )}

      <div className="error-log-controls">
        <div className="filter-group">
          <label>
            Level:
            <select
              value={filter.level || ''}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  level: e.target.value ? (e.target.value as 'error' | 'warning' | 'info') : undefined,
                })
              }
            >
              <option value="">All</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </label>
          <label>
            Type:
            <input
              type="text"
              value={filter.type || ''}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  type: e.target.value || undefined,
                })
              }
              placeholder="Filter by type..."
            />
          </label>
          <label>
            Limit:
            <input
              type="number"
              value={filter.limit}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  limit: parseInt(e.target.value, 10) || 100,
                })
              }
              min="1"
              max="1000"
            />
          </label>
        </div>
        <div className="control-buttons">
          <button onClick={() => { fetchLogs(); fetchStats(); }}>Refresh</button>
          <button
            className={autoRefresh ? 'active' : ''}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading error logs...</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="error-log-list">
        {logs.length === 0 && !loading && (
          <div className="no-logs">No error logs found</div>
        )}
        {logs.map((log) => (
          <div key={log.id} className={`error-log-entry ${log.level}`}>
            <div className="log-header">
              <span className={getLevelBadgeClass(log.level)}>{log.level.toUpperCase()}</span>
              <span className="log-type">{log.type}</span>
              <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
            </div>
            <div className="log-message">
              {log.message}
              {log.details?.flightNumber && (
                <span className="flight-number-badge">Flight: {log.details.flightNumber}</span>
              )}
            </div>
            {log.details && (
              <details className="log-details">
                <summary>Details</summary>
                <div className="log-details-content">
                  {log.details.endpoint && (
                    <div>
                      <strong>Endpoint:</strong> {log.details.method} {log.details.endpoint}
                    </div>
                  )}
                  {log.details.flightNumber && (
                    <div>
                      <strong>Flight Number:</strong> {log.details.flightNumber}
                    </div>
                  )}
                  {log.details.route && (
                    <div>
                      <strong>Route:</strong>{' '}
                      {log.details.route.origin || '?'} →{' '}
                      {log.details.route.destination || '?'}
                      {log.details.route.departureDate && ` on ${log.details.route.departureDate}`}
                    </div>
                  )}
                  {log.details.errorCode && (
                    <div>
                      <strong>Error Code:</strong> {log.details.errorCode}
                    </div>
                  )}
                  {log.details.apiSource && (
                    <div>
                      <strong>API/Data Source:</strong> <span className="api-source-badge">{log.details.apiSource}</span>
                    </div>
                  )}
                  {log.details.requestBody && (
                    <div>
                      <strong>Request Body:</strong>
                      <pre>{JSON.stringify(log.details.requestBody, null, 2)}</pre>
                    </div>
                  )}
                  {log.details.queryParams && (
                    <div>
                      <strong>Query Params:</strong>
                      <pre>{JSON.stringify(log.details.queryParams, null, 2)}</pre>
                    </div>
                  )}
                  {log.details.error && (
                    <div>
                      <strong>Error:</strong> {log.details.error}
                    </div>
                  )}
                  {log.details.stack && (
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="stack-trace">{log.details.stack}</pre>
                    </div>
                  )}
                  {log.userAgent && (
                    <div>
                      <strong>User Agent:</strong> {log.userAgent}
                    </div>
                  )}
                  {log.ip && (
                    <div>
                      <strong>IP:</strong> {log.ip}
                    </div>
                  )}
                  {log.details.basePrice !== undefined && (
                    <div>
                      <strong>Base Price:</strong> {log.details.basePrice}
                    </div>
                  )}
                  {log.details.httpStatus && (
                    <div>
                      <strong>HTTP Status:</strong> {log.details.httpStatus} {log.details.statusText || ''}
                    </div>
                  )}
                  {log.details.requestDuration !== undefined && (
                    <div>
                      <strong>Request Duration:</strong> {log.details.requestDuration}ms
                    </div>
                  )}
                  {log.details.requestStartTime && (
                    <div>
                      <strong>Request Started:</strong> {new Date(log.details.requestStartTime).toLocaleString()}
                    </div>
                  )}
                  {log.details.responseHeaders && Object.keys(log.details.responseHeaders).length > 0 && (
                    <div>
                      <strong>Response Headers:</strong>
                      <pre>{JSON.stringify(log.details.responseHeaders, null, 2)}</pre>
                    </div>
                  )}
                  {log.details.userAgent && (
                    <div>
                      <strong>User Agent:</strong> {log.details.userAgent}
                    </div>
                  )}
                  {log.details.language && (
                    <div>
                      <strong>Language:</strong> {log.details.language}
                    </div>
                  )}
                  {log.details.platform && (
                    <div>
                      <strong>Platform:</strong> {log.details.platform}
                    </div>
                  )}
                  {log.details.onLine !== undefined && (
                    <div>
                      <strong>Online:</strong> {log.details.onLine ? 'Yes' : 'No'}
                    </div>
                  )}
                  {log.details.connectionType && (
                    <div>
                      <strong>Connection Type:</strong> {log.details.connectionType}
                    </div>
                  )}
                  {log.details.url && (
                    <div>
                      <strong>Request URL:</strong> {log.details.url}
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ErrorLog;

